import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ShieldAlert, Bell, ArrowRight, Radar, Sparkles, Send, Loader2, Blocks, CheckCircle2, Globe, Zap, Route } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const SEV = { critical: "#FF3B30", high: "#FF7A45", warning: "#FFD60A", info: "#4da3ff" };

const StatChip = ({ label, value, color }) => (
  <div className="flex items-center gap-2 rounded-lg border border-white/10 bg-black/30 px-3 py-2" data-testid={`attention-count-${label.toLowerCase()}`}>
    <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
    <span className="text-white font-heading text-lg font-bold tabular-nums">{value}</span>
    <span className="text-zinc-500 text-xs uppercase tracking-wide">{label}</span>
  </div>
);

export default function CommandCenter() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { active } = useWorkspace();
  const [feed, setFeed] = useState(null);
  const [loading, setLoading] = useState(true);

  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const chatListRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/attention");
      setFeed(res.data);
    } catch { setFeed(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    const onSwitch = () => load();
    window.addEventListener("workspace-switched", onSwitch);
    return () => window.removeEventListener("workspace-switched", onSwitch);
  }, [load]);
  // Keep the chat pinned to the latest message WITHOUT scrolling the whole page.
  useEffect(() => {
    if (msgs.length === 0 && !sending) return;
    const el = chatListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [msgs, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    const next = [...msgs, { role: "user", content: text }];
    setMsgs(next);
    setInput("");
    setSending(true);
    try {
      const res = await api.post("/assistant", { message: text, history: msgs });
      setMsgs([...next, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assistant unavailable");
      setMsgs([...next, { role: "assistant", content: "Sorry — I couldn't answer that just now. Please try again." }]);
    } finally { setSending(false); }
  };

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const firstName = (user?.name || "").split(" ")[0];
  const counts = feed?.counts || {};
  const items = feed?.items || [];

  const modules = [
    { key: "journey", name: "Modernization Journey", desc: "Scan → Fix → Modernize", icon: Route, path: "/journey", active: true },
    { key: "scanner", name: "Website Scanner", desc: "Security, SEO & uptime", icon: Radar, path: "/dashboard", active: true },
    { key: "integrations", name: "Integrations", desc: "Connect your tools", icon: Blocks, path: "/integrations", active: true },
    { key: "automations", name: "Automations", desc: "Rules that act for you", icon: Zap, path: "/automations", active: true },
  ];

  const suggestions = ["Draft my morning briefing", "What's on my plate today?", "What should I fix first?"];

  return (
    <Layout>
      <div className="mb-6">
        <p className="text-[color:var(--ws-accent,#4da3ff)] text-xs font-semibold uppercase tracking-[0.2em] mb-1" data-testid="cc-eyebrow">
          {active?.name || "Command Center"}
        </p>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white" data-testid="cc-greeting">
          {greeting}{firstName ? `, ${firstName}` : ""}.
        </h1>
        <p className="text-zinc-400 text-base mt-1">Here's what needs your attention today.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Attention feed */}
        <div className="lg:col-span-8 space-y-5">
          <div className="flex flex-wrap gap-2.5" data-testid="attention-summary">
            <StatChip label="Critical" value={counts.critical || 0} color={SEV.critical} />
            <StatChip label="High" value={counts.high || 0} color={SEV.high} />
            <StatChip label="Warning" value={counts.warning || 0} color={SEV.warning} />
            <StatChip label="Info" value={counts.info || 0} color={SEV.info} />
          </div>

          <div className="bg-[#121212] border border-white/10 rounded-xl p-5" data-testid="attention-feed">
            <div className="flex items-center gap-2 mb-4">
              <Bell size={16} className="text-[color:var(--ws-accent,#4da3ff)]" />
              <h2 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Attention Feed</h2>
              <span className="ml-auto text-zinc-500 text-xs">{feed?.domainsTracked || 0} site{(feed?.domainsTracked || 0) === 1 ? "" : "s"} tracked</span>
            </div>

            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#007AFF]" /></div>
            ) : items.length === 0 ? (
              <div className="text-center py-12" data-testid="attention-empty">
                <CheckCircle2 size={40} className="text-[#32D74B] mx-auto mb-3" />
                <p className="text-white font-medium">You're all clear.</p>
                <p className="text-zinc-500 text-sm mt-1">No urgent items right now. Scan a website to start monitoring.</p>
                <button data-testid="cc-scan-cta" onClick={() => navigate("/dashboard")} className="btn-brand text-white rounded-lg px-4 py-2 text-sm mt-4 inline-flex items-center gap-2">
                  <Globe size={15} /> Scan a website
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {items.map((it) => (
                  <button
                    key={it.id}
                    data-testid={`attention-item-${it.id}`}
                    onClick={() => navigate(it.action)}
                    className="w-full text-left border border-white/10 rounded-lg p-3.5 hover:bg-white/5 transition-colors flex items-start gap-3 group"
                    style={{ background: `${SEV[it.severity]}0a` }}
                  >
                    <ShieldAlert size={16} style={{ color: SEV[it.severity] }} className="mt-0.5 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white text-sm font-medium">{it.title}</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ color: SEV[it.severity], background: `${SEV[it.severity]}1a` }}>{it.severity}</span>
                      </div>
                      <p className="text-zinc-400 text-xs mt-1 leading-relaxed">{it.detail}</p>
                    </div>
                    <ArrowRight size={15} className="text-zinc-600 group-hover:text-white transition-colors mt-0.5 shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Modules */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="modules-grid">
            {modules.map((m) => (
              <button
                key={m.key}
                data-testid={`module-${m.key}`}
                onClick={() => navigate(m.path)}
                className="text-left bg-[#121212] border border-white/10 rounded-xl p-5 hover:border-[color:var(--ws-accent,#4da3ff)]/40 transition-colors group"
              >
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-lg bg-[color:var(--ws-accent,#4da3ff)]/15 flex items-center justify-center">
                    <m.icon size={18} className="text-[color:var(--ws-accent,#4da3ff)]" />
                  </div>
                  <ArrowRight size={16} className="text-zinc-600 group-hover:text-white transition-colors" />
                </div>
                <p className="text-white font-medium mt-3">{m.name}</p>
                <p className="text-zinc-500 text-sm">{m.desc}</p>
              </button>
            ))}
          </div>
        </div>

        {/* AI Chief of Staff */}
        <div className="lg:col-span-4">
          <div className="bg-gradient-to-br from-[#141827] to-[#0f1119] border border-[color:var(--ws-accent,#4da3ff)]/25 rounded-xl flex flex-col h-[560px] sticky top-24" data-testid="assistant-panel">
            <div className="flex items-center gap-2 p-4 border-b border-white/10">
              <Sparkles size={16} className="text-[color:var(--ws-accent,#4da3ff)]" />
              <h2 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">AI Chief of Staff</h2>
            </div>
            <div ref={chatListRef} className="flex-1 overflow-y-auto p-4 space-y-3">
              {msgs.length === 0 && (
                <div>
                  <p className="text-zinc-400 text-sm leading-relaxed">Ask me anything about your workspace — I can prioritize your day, summarize risks and tell you what to fix first.</p>
                  <div className="flex flex-col gap-2 mt-3">
                    {suggestions.map((s) => (
                      <button key={s} data-testid="assistant-suggestion" onClick={() => setInput(s)} className="text-left text-xs text-[color:var(--ws-accent,#4da3ff)] border border-[color:var(--ws-accent,#4da3ff)]/25 rounded-lg px-3 py-2 hover:bg-[color:var(--ws-accent,#4da3ff)]/10 transition-colors">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {msgs.map((m, i) => (
                <div key={i} className={`text-sm leading-relaxed ${m.role === "user" ? "text-right" : ""}`} data-testid={`assistant-msg-${m.role}`}>
                  <span className={`inline-block rounded-xl px-3 py-2 whitespace-pre-line ${m.role === "user" ? "bg-[color:var(--ws-accent,#4da3ff)]/20 text-white" : "bg-white/5 text-zinc-200"}`}>
                    {m.content}
                  </span>
                </div>
              ))}
              {sending && <div className="flex items-center gap-2 text-zinc-500 text-sm"><Loader2 size={14} className="animate-spin" /> Thinking…</div>}
            </div>
            <div className="p-3 border-t border-white/10 flex items-center gap-2">
              <input
                data-testid="assistant-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                placeholder="Ask your Chief of Staff…"
                className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-[color:var(--ws-accent,#4da3ff)]/50"
              />
              <button data-testid="assistant-send" onClick={send} disabled={sending || !input.trim()} className="btn-brand text-white rounded-lg p-2.5 disabled:opacity-50">
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
